import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import Button from "../form/Button";
import PrivacyNoticeContent from "./PrivacyNoticeContent";
import { useModalBehavior } from "../../hooks/useModalBehavior";
import { PRIVACY_LANGUAGES, PRIVACY_NOTICES, PRIVACY_UI_TEXT } from "../../constants/privacyNotice";

/**
 * Privacy notice dialog with an English / Bahasa Melayu switch.
 *  - onAgree: user accepted the notice (parent should tick the consent checkbox and close)
 *  - onClose: dismiss only (Close button, X, backdrop or Escape)
 */
const PrivacyNoticeModal = ({ isOpen, language, onLanguageChange, onAgree, onClose }) => {
  const dialogRef = useRef(null);
  const scrollRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const notice = PRIVACY_NOTICES[language];
  const text = PRIVACY_UI_TEXT[language];

  useModalBehavior(isOpen, dialogRef, onClose);

  const updateProgress = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight - el.clientHeight;
    setProgress(scrollable > 0 ? Math.min(100, (el.scrollTop / scrollable) * 100) : 100);
  }, []);

  // Start every language / open at the top of the notice.
  useEffect(() => {
    if (!isOpen) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    updateProgress();
  }, [isOpen, language, updateProgress]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="privacy-backdrop fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-notice-title"
        tabIndex={-1}
        className="privacy-sheet flex h-[90dvh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl outline-none sm:h-auto sm:max-h-[85dvh] sm:rounded-3xl"
      >
        {/* Header */}
        <div className="shrink-0 px-5 pb-3 pt-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <h2 id="privacy-notice-title" className="text-[16px] font-semibold leading-6 text-[#111111]">
              {notice.title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={text.closeLabel}
              className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#1111117A] transition hover:bg-[#F6F3EE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              <IoClose size={20} />
            </button>
          </div>

          <div role="group" aria-label={text.language} className="grid grid-cols-2 gap-1 rounded-full bg-[#F6F3EE] p-1">
            {PRIVACY_LANGUAGES.map(({ code, label }) => {
              const active = code === language;
              return (
                <button
                  key={code}
                  type="button"
                  lang={code}
                  aria-pressed={active}
                  onClick={() => onLanguageChange(code)}
                  className={`rounded-full py-2 text-[12px] font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${
                    active ? "bg-primary text-white shadow-sm" : "text-[#111111B8] hover:text-primary"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Reading progress */}
        <div className="h-0.5 shrink-0 bg-[#11111114]" aria-hidden="true">
          <div className="h-full bg-primary transition-[width] duration-150" style={{ width: `${progress}%` }} />
        </div>

        {/* Notice text */}
        <div
          ref={scrollRef}
          onScroll={updateProgress}
          tabIndex={0}
          role="region"
          aria-label={text.scrollRegion}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 focus-visible:outline-none"
        >
          <PrivacyNoticeContent notice={notice} />
        </div>

        {/* Actions */}
        <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-[#11111126] bg-white px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
          <Button
            type="button"
            eventAction={onClose}
            intent="more"
            size="costum"
            roundness="pill"
            classes="border border-primary bg-white text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {text.close}
          </Button>
          <Button
            type="button"
            eventAction={onAgree}
            intent="primary"
            size="costum"
            roundness="pill"
            classes="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {text.agree}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrivacyNoticeModal;
